import { useState } from 'react'
import { Box, Button, CircularProgress, Menu, MenuItem, Typography } from '@mui/material'
import DownloadIcon from '@mui/icons-material/Download'
import AxiosRequest from '../../../../utils/AxiosRequest'
import config       from '../../../../configuration/configuration.js'

// Dependencies: yarn add html2canvas jspdf

export default function LockdownKPIDashboardExportButton({ containerRef, title = 'Lockdown_KPI_Dashboard' }) {
  const [anchor,  setAnchor]  = useState(null)
  const [loading, setLoading] = useState(false)

  // Capture the full grid as one canvas
  const captureAll = async () => {
    const html2canvas = (await import('html2canvas')).default
    const el = containerRef?.current
    if (!el) { console.warn('[Export] No container found'); return null }
    return await html2canvas(el, {
      scale: 3, useCORS: true,
      backgroundColor: '#ffffff',
      logging: false,
      allowTaint: true,
      imageTimeout: 0,
      width:  el.scrollWidth,
      height: el.scrollHeight,
      windowWidth:  el.scrollWidth,
      windowHeight: el.scrollHeight,
    })
  }

  // Capture each KPI card individually
  const captureEachChart = async () => {
    const html2canvas = (await import('html2canvas')).default
    const el = containerRef?.current
    if (!el) return []

    const cards = Array.from(el.querySelectorAll('.MuiCard-root'))
      .filter(node => node.querySelector('canvas'))

    const results = []
    for (const card of cards) {
      const allTexts = Array.from(card.querySelectorAll('p, span'))
      const titleEl  = allTexts.find(el =>
        el.textContent && el.textContent === el.textContent.toUpperCase() && el.textContent.trim().length > 2
      )
      const name = titleEl?.textContent?.trim() ?? 'KPI'
      const canvas = await html2canvas(card, {
        scale: 3, useCORS: true,
        backgroundColor: '#ffffff',
        logging: false,
        allowTaint: true,
        imageTimeout: 0,
      })
      results.push({ name, imageBase64: canvas.toDataURL('image/jpeg', 0.95) })
    }
    return results
  }

  const exportPDF = async () => {
    setAnchor(null)
    setLoading(true)
    try {
      const { jsPDF } = await import('jspdf')
      const charts    = await captureEachChart()
      if (!charts.length) return

      const margin  = 20
      const spacing = 12
      const pdf = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' })
      const pw  = pdf.internal.pageSize.getWidth()
      const ph  = pdf.internal.pageSize.getHeight()

      const usableW   = pw - margin * 2
      const usableH   = ph - margin * 2 - (charts.length - 1) * spacing
      const hPerChart = usableH / charts.length

      pdf.setFontSize(9)
      pdf.setTextColor(100, 100, 120)
      pdf.text(title, pw / 2, margin / 2, { align: 'center' })

      charts.forEach((chart, i) => {
        const imgProps = pdf.getImageProperties(chart.imageBase64)
        const ratio    = imgProps.width / imgProps.height
        const w        = Math.min(usableW, hPerChart * ratio)
        const h        = w / ratio
        const x        = margin + (usableW - w) / 2
        const y        = margin + i * (hPerChart + spacing)
        pdf.addImage(chart.imageBase64, 'JPEG', x, y, w, h, '', 'FAST')
      })

      pdf.save(`${title}.pdf`)
    } finally { setLoading(false) }
  }

  const exportPNG = async () => {
    setAnchor(null)
    setLoading(true)
    try {
      const canvas  = await captureAll()
      if (!canvas) return
      const link    = document.createElement('a')
      link.download = `${title}.png`
      link.href     = canvas.toDataURL('image/png')
      link.click()
    } finally { setLoading(false) }
  }

  const exportPPT = async () => {
    setAnchor(null)
    setLoading(true)
    try {
      const charts = await captureEachChart()
      if (!charts.length) return

      const res = await AxiosRequest({
        method:       'POST',
        url:          `${config.API_URL}Beacon4Export/Pptx`,
        data:         { title, charts },
        responseType: 'blob',
      })

      const blob = new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      })
      const url  = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href     = url
      link.download = `${title}.pptx`
      link.click()
      URL.revokeObjectURL(url)
    } finally { setLoading(false) }
  }

  return (
    <>
      <Button
        variant="outlined" size="small"
        onClick={e => setAnchor(e.currentTarget)}
        disabled={loading}
        startIcon={loading ? <CircularProgress size={12} /> : <DownloadIcon sx={{ fontSize: 14 }} />}
        sx={{ height: 28, fontSize: 11, textTransform: 'none', px: 1.5 }}
      >
        {loading ? 'Exporting...' : 'Export'}
      </Button>

      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}
        PaperProps={{ sx: { borderRadius: 2, boxShadow: '0 4px 20px rgba(0,0,0,.12)' } }}>
        <MenuItem onClick={exportPDF} sx={{ fontSize: 12, gap: 1 }}>
          <Box sx={{ width: 20, height: 20, bgcolor: '#c53030', borderRadius: 0.5, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography sx={{ fontSize: 8, color: '#fff', fontWeight: 700 }}>PDF</Typography>
          </Box>
          Export as PDF
        </MenuItem>
        <MenuItem onClick={exportPPT} sx={{ fontSize: 12, gap: 1 }}>
          <Box sx={{ width: 20, height: 20, bgcolor: '#d04423', borderRadius: 0.5, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography sx={{ fontSize: 8, color: '#fff', fontWeight: 700 }}>PPT</Typography>
          </Box>
          Export as PowerPoint
        </MenuItem>
        <MenuItem onClick={exportPNG} sx={{ fontSize: 12, gap: 1 }}>
          <Box sx={{ width: 20, height: 20, bgcolor: '#3182ce', borderRadius: 0.5, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography sx={{ fontSize: 8, color: '#fff', fontWeight: 700 }}>PNG</Typography>
          </Box>
          Export as Image
        </MenuItem>
      </Menu>
    </>
  )
}
